"use client"
import { useEffect } from 'react'
import { alpha, Box, Button, Typography, useTheme } from "@mui/material"
import { useTranslation } from '@/app/i18n/client'
import { fallbackLng, defaultNS } from '@/app/i18n/settings'

export default function Error({
  error,
  reset,
}: {
  error: Error & { digest?: string }
  reset: () => void
}) {
  const theme = useTheme()
  // const screenSize = useScreenSize() // plus utilisé
  const { t } = useTranslation(fallbackLng, defaultNS, {})

  // Couleurs inspirées de la palette error
  const circleColors = [theme.palette.error.main, theme.palette.error.dark]

  useEffect(() => {
    // Log the error to an error reporting service
    console.error(error)
  }, [error])

  return (
    <Box
      sx={{
        p: 2,
        m: 0,
        top: 0,
        left: 0,
        zIndex: 2000,
        width: '100vw',
        height: '100vh',
        display: 'flex',
        position: 'fixed',
        alignItems: 'center',
        justifyContent: 'center',
        flexDirection: 'column',
        background: "rgba(17, 25, 40, .35)",
        backdropFilter: "blur(10px) saturate(181%)",
      }}
    >
      {/* Cercles concentriques */}
      <Box
        sx={{
          zIndex: 1,
          left: '50%',
          display: 'flex',
          borderRadius: '50%',
          position: 'absolute',
          alignItems: 'center',
          justifyContent: 'center',
          width: 340,
          height: 340,
          top: '18%',
          transform: 'translateX(-50%)',
          background: `${circleColors[0]}30`,
        }}
      >
        <Box
          sx={{
            zIndex: 2,
            display: 'flex',
            borderRadius: '50%',
            alignItems: 'center',
            justifyContent: 'center',
            width: 220,
            height: 220,
            background: `${circleColors[1]}90`,
          }}
        />
      </Box>
      <Box
        sx={{
          zIndex: 3,
          width: '100%',
          maxWidth: 420,
          mx: 'auto',
          p: 4,
          borderRadius: 6,
          boxShadow: 8,
          background: 'rgba(255,255,255,0.10)',
          backdropFilter: 'blur(8px) saturate(180%)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
        }}
      >
        <Typography
          variant="h2"
          fontWeight={800}
          align="center"
          color="#fff"
          sx={{ mb: 2, textShadow: '0 2px 8px rgba(0,0,0,0.18)' }}
        >
          {t('error.title', 'Oops!')}
        </Typography>
        <Typography
          variant="h5"
          align="center"
          color="rgba(255,255,255,0.92)"
          sx={{ mb: 2 }}
        >
          {t('error.subtitle', 'Une erreur inattendue est survenue')}
        </Typography>
        <Typography
          variant="body1"
          align="center"
          color="rgba(255,255,255,0.8)"
          sx={{ mb: 3 }}
        >
          {t('error.description', 'Veuillez réessayer ou contacter le support si le problème persiste.')}
        </Typography>
        <Button
          size="large"
          variant="black-glass"
          onClick={() => reset()}
          sx={{
            my: 1,
            height: 48,
            minWidth: 160,
            fontWeight: 700,
            fontSize: '1.1rem',
            borderRadius: 4,
            textTransform: 'none'
          }}
        >
          {t('error.retry')}
        </Button>
      </Box>
    </Box>
  )
}
